/**
 * Advanced-list MATERIALIZED Membership Entity Schemas (plan 2026-06-09-001).
 *
 * Distinct from `list-member.ts` (the LIVE, BB-call resolver contract). These
 * schemas describe the DB-sourced materialized snapshot read by the admin
 * display endpoint (U8) and the reverse-index endpoint (U9): counts, match-rate,
 * staleness, derived display state, and a capped member preview — all served
 * WITHOUT a live Blackbaud call so display survives a dead admin token.
 *
 * Mirrors `list-member.ts`'s match-status vocabulary so the two contracts stay
 * aligned (`MATCHED | AMBIGUOUS | UNMATCHED_NO_KEY | UNMATCHED_NO_USER`).
 */

import { z } from 'zod'

/**
 * Derived display state for a materialized list (U8). The state is computed
 * server-side from the snapshot + sidecar so the web admin doesn't re-derive it
 * (and so suppression rules — e.g. hide match-rate when truncated — are enforced
 * in one place).
 *
 *  - NEVER_SYNCED            no snapshot has ever been written (no activeGeneration)
 *  - SYNCED_EMPTY            last sync succeeded but the list had 0 rows
 *  - STRUCTURALLY_UNJOINABLE rows > 0 but 0 resolved users (no joinable column)
 *  - LOW_MATCH               rows resolve, but match-rate is below a healthy floor
 *  - TRUNCATED               snapshot hit the page cap; counts are a lower bound,
 *                            match-rate is suppressed
 *  - DEGRADED                last sync FAILED; prior snapshot served with a
 *                            staleness age + a token-health deep link
 *  - HEALTHY                 synced, rows resolve at a healthy match-rate
 */
export const ListMembershipDisplayStateSchema = z.enum([
  'NEVER_SYNCED',
  'SYNCED_EMPTY',
  'STRUCTURALLY_UNJOINABLE',
  'LOW_MATCH',
  'TRUNCATED',
  'DEGRADED',
  'HEALTHY'
])

export type ListMembershipDisplayState = z.infer<typeof ListMembershipDisplayStateSchema>

/**
 * A single resolved member in the preview (U8). Sourced from our own DB
 * (`User` for name/publicId, `UsersOnSchools` for the school-scoped photo);
 * never from the raw BB row blob. Admin-only surface.
 */
export const ListMembershipPreviewMemberSchema = z.object({
  userPublicId: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  photoUrl: z.string().nullable()
})

export type ListMembershipPreviewMember = z.infer<typeof ListMembershipPreviewMemberSchema>

/**
 * One designable column on the list (U8 → U10 designation picker). Enumerated
 * from a representative row of the active snapshot; `ordinal` is the
 * authoritative zero-based position (KTD9) and `label` carries an ordinal suffix
 * when a column name repeats so duplicates are distinguishable. Never includes
 * the row VALUE — header names only (admin picks the recipient column by name).
 */
export const ListMembershipColumnSchema = z.object({
  ordinal: z.number().int().min(0),
  name: z.string(),
  label: z.string()
})

export type ListMembershipColumn = z.infer<typeof ListMembershipColumnSchema>

/**
 * The materialized snapshot display block (U8). Counts come straight from the
 * sidecar; the preview is a capped slice of the resolved members + a separate
 * unmatched-row count. `matchRate` is null when suppressed (truncated) or
 * undefined-by-state (never-synced).
 */
export const ListMembershipSnapshotSchema = z.object({
  /** Whether the list has a confirmed designated column (KTD4a) — targetable. */
  targetable: z.boolean(),
  /** COUNT(rows) in the active snapshot. */
  bbRowCount: z.number().min(0),
  /** COUNT(DISTINCT user) over the designated column. */
  resolvedUserCount: z.number().min(0),
  /** resolvedUserCount / bbRowCount, 0..1; null when suppressed/inapplicable. */
  matchRate: z.number().min(0).max(1).nullable(),
  /** Snapshot hit the defensive page cap — counts are a lower bound. */
  truncated: z.boolean(),
  /** Derived display state (single source of truth for the UI). */
  displayState: ListMembershipDisplayStateSchema,
  /** Last terminal sync status (SUCCESS / EMPTY / FAILED / …); null if never run. */
  lastSyncStatus: z.string().nullable(),
  /** ISO timestamp of the last SUCCESSFUL sync (staleness anchor); null if never. */
  lastSyncCompletedAt: z.string().nullable(),
  /** Whole-seconds since the last success; null when never-synced. */
  stalenessAgeSeconds: z.number().min(0).nullable(),
  /** True when the last attempt FAILED → surface a token-health deep link. */
  degraded: z.boolean(),
  /** Capped slice of resolved members for a face-pile / preview. */
  previewMembers: z.array(ListMembershipPreviewMemberSchema),
  /** previewMembers cap; resolvedUserCount may exceed this (overflow). */
  previewCap: z.number().min(0),
  /** Rows that did NOT resolve to a user (no-key / no-user / ambiguous). */
  unmatchedRowCount: z.number().min(0),
  /**
   * Designable columns enumerated from a representative row of the active
   * snapshot (U10 designation picker, KTD9). Empty when never-synced or no row
   * is available; header NAMES only (no values).
   */
  columns: z.array(ListMembershipColumnSchema),
  /** Currently designated column name (KTD4a), if confirmed; null otherwise. */
  designatedColumnName: z.string().nullable(),
  /** Currently designated column ordinal, if confirmed; null otherwise. */
  designatedColumnOrdinal: z.number().int().min(0).nullable()
})

export type ListMembershipSnapshot = z.infer<typeof ListMembershipSnapshotSchema>

/**
 * A single materialized list a person appears on (U9 reverse index). Only lists
 * the person joins via an EXACT MATCHED row appear — AMBIGUOUS + name-only are
 * excluded by construction (so the answer is a lower bound; see `incomplete`).
 */
export const PersonListMembershipSchema = z.object({
  listPublicId: z.string(),
  listName: z.string().nullable(),
  /** ISO timestamp of the last successful sync for this list (per-list staleness). */
  lastSyncCompletedAt: z.string().nullable()
})

export type PersonListMembership = z.infer<typeof PersonListMembershipSchema>
